import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import { financeSyncSchema } from "../../../shared/account-finance/finance-sync-contract";
import { workbookDashboardSchema } from "../../../shared/account-finance/finance-workbook-contract";
import type { WorkbookDashboard } from "../../../shared/account-finance/finance-workbook-contract";
import { apiRequest } from "../../shared/api/client";

const responseSchema = z.object({ job: financeSyncSchema.nullable(), book: workbookDashboardSchema.nullable() });
export function useFinanceSync(date: string, company: string | undefined, accept: (book: WorkbookDashboard) => void) {
  const [message, setMessage] = useState<string | null>(null), [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  const pending = useRef(false);
  const refresh = useCallback(() => { if (!pending.current) { pending.current = true; setVersion(value => value + 1); } }, []);
  useEffect(() => {
    if (!company) return;
    const controller = new AbortController(); let timer: ReturnType<typeof setTimeout>;
    const read = async (start: boolean, force = false) => {
      try {
        const result = responseSchema.parse(await apiRequest<unknown>(`/api/account-finance/workbook/sync${start ? "" : `?date=${date}`}`, {
          ...(start ? { method: "POST", body: JSON.stringify({ date, force }) } : {}),
          cache: "no-store", signal: AbortSignal.any([controller.signal, AbortSignal.timeout(15000)]),
        }));
        if (controller.signal.aborted) return;
        if (result.book?.date === date && result.book.company === company) accept(result.book);
        const job = result.job;
        if (job && job.company !== company) throw new Error("Finans şirket kapsamı eşleşmedi.");
        const running = job?.phase === "running";
        pending.current = Boolean(running); setBusy(Boolean(running));
        setMessage(job?.date === date ? job.message : running ? "Diğer raporun güncellemesi bekleniyor…" : null);
        timer = setTimeout(() => void read(!running), running ? 5000 : 300000);
      } catch {
        if (!controller.signal.aborted) {
          pending.current = false; setBusy(false); setMessage("Güncelleme beklemede. Son kayıtlı tablo korunuyor.");
          // Status polling is local-only; it never retries an ERP failure.
          timer = setTimeout(() => void read(false), 15000);
        }
      }
    };
    pending.current = true; setBusy(true); void read(true, version > 0);
    return () => { controller.abort(); clearTimeout(timer); pending.current = false; };
  }, [date, company, accept, version]);
  return { syncMessage: message, syncing: busy, refresh };
}
